import { SingleSelectOption } from 'uiComponents/input'
import {
    endpoints,
    useGetAllArticlesQuery,
    useGetAllCategoriesQuery,
    useGetProductAdditionalInfoQuery,
} from './reduxApi'
import { CategoryFromApi } from './types'
import { useParams } from 'react-router-dom'
import { BaseRouteParams } from '../hocs'
import { useMemo } from 'react'
import { State } from '../store'
import { getProductName } from './productTable/utils'
import { Option } from 'uiComponents/combobox/combobox'
import { categoryTypeIconMap, productTypeIconMap } from 'utils/lucideIcon'
import { getProductTypeNameMap } from './crud/utils'
import { CheckoutFlowNameMap } from 'admin/articleService'
import { useTranslation } from 'translations'

interface CategoryProductType {
    type: 'article' | 'category'
}

const sortByPriority = (a: any, b: any) => a.priority - b.priority
const sortArrayByPriority = <T>(arr: T[]): T[] => {
    return [...arr].sort(sortByPriority)
}

// Sort numerically descending, then alphabetically
const sortByEyebrow = (a: any, b: any) => {
    if (a.eyebrow && b.eyebrow) {
        const aMatch = a.eyebrow.match(/^(\d+)/)
        const bMatch = b.eyebrow.match(/^(\d+)/)

        if (aMatch && bMatch) {
            const aNum = parseInt(aMatch[1], 10)
            const bNum = parseInt(bMatch[1], 10)
            return bNum - aNum
        } else if (aMatch && !bMatch) {
            return -1
        } else if (!aMatch && bMatch) {
            return 1
        } else {
            return a.eyebrow.localeCompare(b.eyebrow)
        }
    }
    if (a.eyebrow && !b.eyebrow) {
        return -1
    }
    if (!a.eyebrow && b.eyebrow) {
        return 1
    }
    return 0
}

export const useIsLoadingCategoriesOrArticles = () => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { isFetching: isFetchingCategories } = useGetAllCategoriesQuery({ account: accountSlug })
    const { isFetching: isFetchingArticles } = useGetAllArticlesQuery({ account: accountSlug })

    return isFetchingCategories || isFetchingArticles
}

export const useGetAllCategories = () => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { data } = useGetAllCategoriesQuery({ account: accountSlug })

    return useMemo(() => sortArrayByPriority(data || []), [data])
}

export const useGetAllArticles = () => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { data } = useGetAllArticlesQuery({ account: accountSlug })

    return useMemo(() => sortArrayByPriority(data || []), [data])
}

export const isLoadingArticlesAndCategories = (state: State) =>
    endpoints.getAllCategories.select({
        account: state.preferences.activeAccount as string,
    })(state).isLoading ||
    endpoints.getAllArticles.select({
        account: state.preferences.activeAccount as string,
    })(state).isLoading

export const useGetAllParentCategories = () => {
    return useGetAllCategories()?.filter((category) => !category.parentCategory?.id)
}

export const useGetAllCategoriesByParentId = (categoryId?: string) => {
    const categories = useGetAllCategories()

    if (!categoryId) {
        return []
    }

    return categories?.filter((category) => category.parentCategory?.id === categoryId)
}

export const useGetAllArticlesByParentId = (categoryId?: string) => {
    const allArticles = useGetAllArticles()

    if (!categoryId) {
        return []
    }

    return allArticles?.filter((article) => !!article.categories.find((c) => c.id === categoryId))
}

export const useGetAllCategoriesAndArticlesById = (id?: string) => {
    const allParentCategories = useGetAllParentCategories()
    const allParentCategoriesById = useGetAllCategoriesByParentId(id)
    const allArticlesByParentId = useGetAllArticlesByParentId(id)

    if (!id) {
        return allParentCategories
    }

    return [...allParentCategoriesById, ...allArticlesByParentId]
}

export const createGetCategoryAncestors = (categories: CategoryFromApi[]) => {
    const categoryRecords = categories?.reduce((acc, category) => {
        acc[category.id] = category
        return acc
    }, {} as Record<string, CategoryFromApi>)

    const getCategoryAncestors = (id: string): CategoryFromApi[] => {
        const child = categoryRecords[id]

        if (!child) {
            return []
        }

        if (!child?.parentCategory?.id) {
            return [child]
        }

        return [...getCategoryAncestors(child.parentCategory.id), child]
    }

    return getCategoryAncestors
}

interface CategoryMap {
    [key: string]: { name: string; parentId: string | undefined }
}

export const getCategoryMap = (categories: CategoryFromApi[]): CategoryMap => {
    return useMemo(() => {
        const categoryMap = {} as CategoryMap
        categories.forEach((category) => {
            categoryMap[category.id] = {
                name: getProductName(category.name),
                parentId: category.parentCategory?.id,
            }
        })
        return categoryMap
    }, [categories])
}

export const getBreadcrumbs = (categoryMap: CategoryMap, categoryId?: string): string[] => {
    const category = categoryId ? categoryMap[categoryId] : null
    if (!category) {
        return []
    }

    const parentBreadcrumbs = category.parentId ? getBreadcrumbs(categoryMap, category.parentId) : []
    return [...parentBreadcrumbs, category.name]
}

export const useGetAllCategoriesForSelect = ({ leavesOnly = false }: { leavesOnly?: boolean } = {}) => {
    const categories = useGetAllCategories()
    const categoryMap = getCategoryMap(categories)

    return useMemo(() => {
        const mappedCategories = categories.map((category) => {
            const breadcrumbs = category.parentCategory ? getBreadcrumbs(categoryMap, category.parentCategory?.id) : []
            const eyebrow = breadcrumbs?.join(' / ')
            return {
                label: getProductName(category.name),
                value: category.id,
                type: 'category',
                iconName: categoryTypeIconMap[category.checkoutFlow],
                eyebrow,
                title: eyebrow,
                iconTitle: CheckoutFlowNameMap[category.checkoutFlow],
                isLeaf: !categories.find((c) => c.parentCategory?.id === category.id),
            }
        })
        const relevantCategories = leavesOnly
            ? mappedCategories.filter((category) => category.isLeaf)
            : mappedCategories
        return relevantCategories.sort(sortByEyebrow)
    }, [categories])
}

export const useGetAllCategoriesForSelectOld = () => {
    const categories = useGetAllCategories()

    return useMemo(
        () =>
            categories?.map((category) => ({
                name: getProductName(category.name),
                value: category.id,
                parentId: category.parentCategory?.id,
            })) || [],
        [categories],
    )
}

export const useGetAllProductsForSelect = (): Option[] => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const categoryMap = getCategoryMap(useGetAllCategories())
    const products = useGetAllArticles()
    const { data: additionalInfo } = useGetProductAdditionalInfoQuery({ account: accountSlug })

    const numericIdByUuid = useMemo(
        () => new Map(additionalInfo?.map((info) => [info.uuid, info.id])),
        [additionalInfo],
    )

    return useMemo(() => {
        const productTypeNameMap = getProductTypeNameMap()
        return products
            .map((product) => {
                const allProductCategories = [...product.categories, ...product.inactiveCategories]
                let eyebrow = ''
                let title = ''
                if (allProductCategories?.length === 1) {
                    eyebrow = getBreadcrumbs(categoryMap, allProductCategories[0]?.id)?.join(' / ')
                    title = eyebrow
                } else if (allProductCategories?.length > 1) {
                    eyebrow = t('products.redux.categoriesCount', { number: allProductCategories.length })
                    const breadcrumbsList = allProductCategories.slice(0, 20).map((category) => {
                        const categoryBreadcrumbs = getBreadcrumbs(categoryMap, category.id)
                        return `- ${categoryBreadcrumbs?.join(' / ')}`
                    })
                    title = breadcrumbsList.join('\n')
                    if (allProductCategories.length > 20) {
                        title += `\n${t('products.redux.moreCategories', { number: allProductCategories.length - 20 })}`
                    }
                }

                const numericId = numericIdByUuid.get(product.id)

                return {
                    label: getProductName(product.name),
                    value: product.id,
                    iconName: productTypeIconMap[product.businessType],
                    eyebrow,
                    title: title.trim(),
                    iconTitle: productTypeNameMap[product.businessType],
                    secondaryLabel: numericId !== undefined ? `#${numericId}` : undefined,
                } as Option
            })
            .sort(sortByEyebrow)
    }, [products, categoryMap, numericIdByUuid, t])
}

export const useGetFullProductsAndArticlesListForSelect = () => {
    const allCategories = useGetAllCategories()
    const allArticles = useGetAllArticles()

    const categoriesById = allCategories?.map((category) => ({
        name: getProductName(category.name),
        value: category.id,
        parentId: category.parentCategory?.id,
        type: 'category',
    }))

    const articlesById = allArticles?.reduce((acc, article) => {
        if (article.categories?.length > 0) {
            article.categories.forEach((category) => {
                acc.push({
                    name: getProductName(article.name),
                    value: article.id,
                    parentId: category.id,
                    type: 'article',
                })
            })
        } else {
            acc.push({
                name: getProductName(article.name),
                value: article.id,
                parentId: undefined,
                type: 'article',
            })
        }

        return acc
    }, [] as (SingleSelectOption & CategoryProductType)[])

    return [...categoriesById, ...articlesById]
}

export const useGetFullProductsAndArticlesNestedTree = () => {
    const allCategories = useGetAllCategories()
    const allArticles = useGetAllArticles()
    const isLoading = useIsLoadingCategoriesOrArticles()

    const categoriesById = allCategories?.map((category) => ({
        name: getProductName(category.name),
        value: category.id,
        parentId: category.parentCategory?.id,
        type: 'category',
        slug: category.id,
    }))

    const articlesById = allArticles?.reduce((acc, article) => {
        if (article.categories?.length > 0 || article.inactiveCategories?.length > 0) {
            ;[...article.categories, ...article.inactiveCategories].forEach((category) => {
                acc.push({
                    name: getProductName(article.name),
                    value: article.id,
                    parentId: category.id,
                    type: 'article',
                })
            })
        } else {
            acc.push({
                name: getProductName(article.name),
                value: article.id,
                parentId: undefined,
                type: 'article',
            })
        }

        return acc
    }, [] as (SingleSelectOption & CategoryProductType)[])

    const nodes: Record<string, any> = {}

    for (const category of categoriesById) {
        nodes[category.value] = {
            id: category.value,
            uuid: category.value,
            name: category.name,
            slug: category.value,
            articles: [],
            children: [],
        }
    }

    for (const article of articlesById) {
        const article_node = {
            id: article.value,
            uuid: article.value,
            name: article.name,
            slug: article.value,
        }

        if (article.parentId && nodes[article.parentId]) {
            nodes[article.parentId].articles.push(article_node)
        }
    }

    const root = []

    for (const category of categoriesById) {
        const node = nodes[category.value]

        if (category.parentId) {
            if (nodes[category.parentId]) {
                nodes[category.parentId].children.push(node)
            }
        } else {
            root.push(node)
        }
    }

    return {
        products: root,
        isLoading,
    }
}
